import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Star, MousePointerClick, CheckCircle2,
  DollarSign, Trophy, Send, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "sent", label: "Sent" },
  { id: "clicked", label: "Clicked" },
  { id: "reviewed", label: "Reviewed" },
  { id: "bonus_owed", label: "Owed" },
  { id: "bonus_paid", label: "Paid" },
];

const RANGE_OPTIONS = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
  { id: 365, label: "1y" },
];

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ByMessagePanel({ stats }) {
  const bm = stats.by_message;
  if (!bm) return null;
  const Cell = ({ label, data, sent, testid }) => (
    <div className="flex-1 rounded-lg bg-zinc-50 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">{label}</div>
      <div className="text-xs text-zinc-500 mb-1">{sent} sent</div>
      <div className="flex gap-4 text-sm">
        <div><span className="font-bold text-blue-700">{data.clicked}</span> <span className="text-zinc-500">clicked</span> <span className="text-[11px] text-zinc-400">({data.click_rate_pct}%)</span></div>
        <div><span className="font-bold text-yellow-700">{data.reviewed}</span> <span className="text-zinc-500">reviewed</span></div>
      </div>
    </div>
  );
  return (
    <div className="bg-white rounded-xl border p-4 mb-6" data-testid="review-by-message">
      <div className="text-sm font-semibold text-zinc-800 mb-3">Which text got the click?</div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Cell label="First text" data={bm.first} sent={stats.sent} testid="by-message-first" />
        <Cell label="5-day follow-up" data={bm.follow_up} sent={stats.followups_sent} testid="by-message-followup" />
      </div>
      {bm.reviewed_without_click > 0 && (
        <div className="text-[11px] text-zinc-500 mt-2">{bm.reviewed_without_click} reviewed without clicking a tracked link.</div>
      )}
    </div>
  );
}

function ClickedViaBadge({ row }) {
  if (!row.clicked_via) return null;
  const follow = row.clicked_via === "follow_up";
  return (
    <span
      data-testid={`clicked-via-${row.tracking_id}`}
      className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${follow ? "bg-purple-100 text-purple-700" : "bg-blue-50 text-blue-700"}`}
    >
      {follow ? "via follow-up" : "via first text"}
    </span>
  );
}

function StatusBadge({ row }) {
  if (row.bonus_paid_at) {
    return <span className="text-[10px] font-bold uppercase tracking-wide bg-zinc-200 text-zinc-700 px-2 py-0.5 rounded-full">Paid</span>;
  }
  if (row.review_confirmed_at) {
    return <span className="text-[10px] font-bold uppercase tracking-wide bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Reviewed</span>;
  }
  if (row.clicked_at) {
    return <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Clicked</span>;
  }
  return <span className="text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full">Sent</span>;
}

export default function ReviewTracking() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [days, setDays] = useState(30);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const [statsRes, rowsRes] = await Promise.all([
        axios.get(`${API}/review-tracking/stats?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/review-tracking?days=${days}&status=${statusFilter}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setStats(statsRes.data);
      setRows(rowsRes.data);
    } catch (err) {
      toast.error("Failed to load review tracking data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [days, statusFilter]);

  const flip = async (tracking_id, action) => {
    try {
      const token = localStorage.getItem("session_token");
      await axios.post(`${API}/review-tracking/${tracking_id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Updated");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
              Review Tracking
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Track who clicked their review link, who left a review, and how much you owe each tech in $15 bonuses.
            </p>
          </div>
          {/* Time-range pill switcher */}
          <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg text-xs flex-shrink-0">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDays(opt.id)}
                data-testid={`range-${opt.id}`}
                className={`px-2.5 py-1 rounded-md font-medium transition ${
                  days === opt.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <KPI icon={<Send className="w-4 h-4" />} label="Sent" value={stats.sent} bg="bg-zinc-100" fg="text-zinc-700" testid="kpi-sent" />
            <KPI
              icon={<MousePointerClick className="w-4 h-4" />}
              label="Clicked"
              value={stats.clicked}
              sub={`${stats.click_rate_pct}% CTR`}
              bg="bg-blue-100" fg="text-blue-700" testid="kpi-clicked"
            />
            <KPI
              icon={<Star className="w-4 h-4" />}
              label="Reviewed"
              value={stats.reviewed}
              sub={`${stats.review_rate_pct}% CR`}
              bg="bg-yellow-100" fg="text-yellow-700" testid="kpi-reviewed"
            />
            <KPI
              icon={<DollarSign className="w-4 h-4" />}
              label="Bonus Owed"
              value={`$${stats.bonus_owed_total.toFixed(0)}`}
              sub={stats.bonus_paid_total > 0 ? `+$${stats.bonus_paid_total.toFixed(0)} paid` : null}
              bg="bg-green-100" fg="text-green-700" testid="kpi-owed"
            />
          </div>
        )}

        {stats && <ByMessagePanel stats={stats} />}

        {/* Leaderboard */}
        {stats && stats.leaderboard.length > 0 && (
          <div className="bg-white rounded-xl border p-5 mb-6">
            <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Tech Leaderboard
            </h3>
            <div className="space-y-2">
              {stats.leaderboard.map((row, idx) => (
                <div key={row.tech_name} className="flex items-center gap-3 py-2 border-b last:border-b-0">
                  <div className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-700 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-zinc-900">{row.tech_name}</div>
                    <div className="text-xs text-zinc-500">{row.reviews} review{row.reviews === 1 ? "" : "s"}</div>
                  </div>
                  <div className="text-right">
                    {row.bonus_owed > 0 && (
                      <div className="text-sm font-semibold text-green-700">${row.bonus_owed.toFixed(0)} owed</div>
                    )}
                    {row.bonus_paid > 0 && (
                      <div className="text-xs text-zinc-500">${row.bonus_paid.toFixed(0)} paid</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status filter tabs */}
        <div className="overflow-x-auto -mx-4 px-4 mb-3 flex gap-2 no-scrollbar">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
                statusFilter === tab.id
                  ? "bg-[#207024] text-white"
                  : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Rows */}
        <div className="space-y-2">
          {rows.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-zinc-500 text-sm">
              No review requests in this range yet.
            </div>
          ) : rows.map((row) => (
            <div key={row.tracking_id} className="bg-white rounded-xl border p-4" data-testid={`row-${row.tracking_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-900 truncate">{row.customer_name}</div>
                  <div className="text-xs text-zinc-500">
                    Sent {formatDateTime(row.sent_at)}
                    {row.tech_names?.length > 0 && (
                      <> • {row.tech_names.join(" & ")}</>
                    )}
                  </div>
                </div>
                <StatusBadge row={row} />
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs text-zinc-600 mb-3 bg-zinc-50 rounded-lg p-2">
                <div>
                  <div className="text-zinc-400 uppercase text-[10px]">Clicked</div>
                  <div className="font-medium">{row.clicked_at ? formatDateTime(row.clicked_at) : "—"}</div>
                  <ClickedViaBadge row={row} />
                </div>
                <div>
                  <div className="text-zinc-400 uppercase text-[10px]">Reviewed</div>
                  <div className="font-medium">{row.review_confirmed_at ? formatDateTime(row.review_confirmed_at) : "—"}</div>
                </div>
                <div>
                  <div className="text-zinc-400 uppercase text-[10px]">Paid</div>
                  <div className="font-medium">{row.bonus_paid_at ? formatDateTime(row.bonus_paid_at) : "—"}</div>
                </div>
              </div>
              {row.followup_status && (
                <div className="text-[11px] text-zinc-500 -mt-2 mb-3" data-testid={`followup-status-${row.tracking_id}`}>
                  {row.followup_status === "sent"
                    ? `Follow-up text sent ${formatDateTime(row.followup_sent_at)}`
                    : row.followup_status === "customer_replied"
                      ? "Follow-up skipped: customer texted back"
                      : `Follow-up skipped (${row.followup_status.replace(/_/g, " ")})`}
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                {!row.review_confirmed_at ? (
                  <button
                    onClick={() => flip(row.tracking_id, "mark-reviewed")}
                    data-testid={`btn-mark-reviewed-${row.tracking_id}`}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-yellow-500 text-white hover:bg-yellow-600"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark Review Left
                  </button>
                ) : row.bonus_eligible === false ? (
                  <>
                    <span className="text-xs text-zinc-500 self-center" data-testid={`no-bonus-${row.tracking_id}`}>
                      No bonus{row.review_stars ? ` · ${row.review_stars}★` : ""} ({row.bonus_ineligible_reason})
                    </span>
                    <button
                      onClick={() => flip(row.tracking_id, "unmark-reviewed")}
                      data-testid={`btn-unmark-reviewed-${row.tracking_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Unmark Reviewed
                    </button>
                  </>
                ) : !row.bonus_paid_at ? (
                  <>
                    <button
                      onClick={() => flip(row.tracking_id, "mark-bonus-paid")}
                      data-testid={`btn-mark-paid-${row.tracking_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700"
                    >
                      <DollarSign className="w-3.5 h-3.5" /> Mark Bonus Paid
                    </button>
                    <button
                      onClick={() => flip(row.tracking_id, "unmark-reviewed")}
                      data-testid={`btn-unmark-reviewed-${row.tracking_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Unmark Reviewed
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => flip(row.tracking_id, "unmark-bonus-paid")}
                    data-testid={`btn-unmark-paid-${row.tracking_id}`}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Unmark Paid
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({ icon, label, value, sub, bg, fg, testid }) {
  return (
    <div className="bg-white rounded-xl border p-4" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${bg} ${fg} mb-2`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-zinc-900 leading-none">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}
